// #region Imports

import * as React from 'react';
import * as Modal from 'react-modal';
import * as objectAssign from 'object-assign';
import * as responsiveness from '../modules/ui/responsiveness'
import { connect } from 'react-redux';
import { IStateTree } from '../redux/store';
import { actionCreators, editNote, updateNoteCollisionAndBundling, setEditingNoteId } from '../redux/modules/document';
import { INoteData } from '../dto/INoteData';
import { INoteCategory } from '../dto/INoteCategory';
import { NoteService } from '../services/noteService';
import CreateNewCategoryModal from './createNewCategoryModal';
import { GUID_EMPTY } from '../common/consts';
import noteSidebar from './noteSidebar';
import { Toaster } from '../modules/ui/toaster';
import UnsavedChangesModal from './unsavedChangesMotal';
import { noteTextToReact, noteTextToEditableHtml, domToNoteText, FormatType } from '../modules/helpers/noteTextFormatter';

// #endregion Imports

// #region Format toolbar icons

const BoldIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M5 3.33333H11.6667C12.5507 3.33333 13.3986 3.68452 14.0237 4.30964C14.6488 4.93477 15 5.78261 15 6.66667C15 7.55072 14.6488 8.39857 14.0237 9.02369C13.3986 9.64881 12.5507 10 11.6667 10H5V3.33333Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 10H12.5C13.3841 10 14.2319 10.3512 14.857 10.9763C15.4821 11.6014 15.8333 12.4493 15.8333 13.3333C15.8333 14.2174 15.4821 15.0652 14.857 15.6904C14.2319 16.3155 13.3841 16.6667 12.5 16.6667H5V10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ItalicIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M15.8333 3.33333H8.33333M11.6667 16.6667H4.16667M12.25 3.91667L7.66667 16.1667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const UnderlineIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M5 2.5V8.33333C5 9.65942 5.52678 10.9312 6.46447 11.8689C7.40215 12.8065 8.67392 13.3333 10 13.3333C11.3261 13.3333 12.5979 12.8065 13.5355 11.8689C14.4732 10.9312 15 9.65942 15 8.33333V2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.33333 17.5H16.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// #endregion Format toolbar icons

// #region Dispatchers

interface INoteInlineEditorComponentDispatch {
    disableEditor?: () => void;
    enableEditor?: () => void;
    onCancelClick?: () => void;
    onNoteCreated?: (note: INoteData) => void;
    onNoteSaved?: (note: INoteData) => void;
    onNoteEditCalled?: (note: INoteData) => void;
    setNoteCategories?: (categories: INoteCategory[]) => void;
    hideToolbar?: () => void;
    setUpdateNotes?: () => void;
    updateNoteBundle?: (index: number) => void;
    forceEditNoteFunc?: (note: INoteData | null) => void;
    setEditingNoteId?: (noteId: string | null) => void;
}

// #endregion Dispatchers

// #region Props

interface INoteInlineEditorComponentProps extends INoteInlineEditorComponentDispatch {
    editorDisabled: boolean,
    note: INoteData;
    noteCategories: INoteCategory[];
    categories: INoteCategory[];
    overlapsSibling: boolean;
    isBundled: boolean;
    forceEditNote?: INoteData;
    editingNoteId?: string | null;
    belongsToBundle?: string;
    updateNoteCollisionAndBundling?: number;
    notesPanelExpaned?: boolean;
    onSaveSuccess?: () => void;
    onCancel?: () => void;
    onDelete?: () => void;
}

// #endregion Props

// #region State

interface INoteInlineEditorComponentState {
    color: string;
    text: string;
    title: string;
    selectedCategoryId?: string;
    isCreateCategoryDialogOpen: boolean;
    unsavedChangesDialogOpen: boolean;
    blockUnsavedDialogs: boolean;
    isInEditMode: boolean;
    isInExpandedMode: boolean;
    clickOutsideHandlerIsPaused: boolean;
    textExpanded: boolean;
    textOverflows: boolean;
    activeFormats: { bold: boolean; italic: boolean; underline: boolean };
}

// #endregion State

// #region Component

class NoteInlineEditorComponent extends React.Component<INoteInlineEditorComponentProps, INoteInlineEditorComponentState> {
    noteEditorRef: React.RefObject<unknown>;
    private textContentEl: HTMLDivElement | null = null;
    private resizeObserver: ResizeObserver | null = null;

    constructor(props) {
        super(props);

        this.noteEditorRef = React.createRef();
        this.handleClickOutsideNote = this.handleClickOutsideNote.bind(this);

        this.state = {
            color: this.props.note.color,
            text: this.props.note.text,
            title: this.props.note.title,
            selectedCategoryId: this.props.note.categoryId || GUID_EMPTY,
            isCreateCategoryDialogOpen: false,
            unsavedChangesDialogOpen: false,
            blockUnsavedDialogs: true,
            isInEditMode: false,
            isInExpandedMode: !this.props.overlapsSibling && (this.props.note.text ? this.props.note.text.length > 24 : false),
            clickOutsideHandlerIsPaused: false,
            textExpanded: false,
            textOverflows: false,
            activeFormats: { bold: false, italic: false, underline: false }
        };
    }

    // Callback-ref på text-elementet. Kopplar en ResizeObserver så att overflow räknas
    // om när kortets bredd ändras (t.ex. när panelen expanderas och korten blir bredare) –
    // annars kan "Visa mer" ligga kvar fast texten ryms på ≤8 rader vid den nya bredden.
    private setTextContentRef = (el: HTMLDivElement | null) => {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        this.textContentEl = el;
        if (el && typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.measureTextOverflow());
            this.resizeObserver.observe(el);
        }
    }

    // Mäter om texten överstiger den klampade höjden (8 rader). Mäts bara i klampat
    // läge; när texten är expanderad behålls föregående värde så "Dölj" inte försvinner.
    private measureTextOverflow = () => {
        if (this.state.textExpanded) return;
        const el = this.textContentEl;
        if (!el) return;
        const overflows = el.scrollHeight > el.clientHeight + 1;
        if (overflows !== this.state.textOverflows) {
            this.setState({ textOverflows: overflows });
        }
    }

    private onToggleTextExpanded = () => {
        this.setState(prev => ({ textExpanded: !prev.textExpanded }), () => {
            // Korthöjden ändrades – be panelen stapla om så korten inte överlappar
            this.props.updateNoteBundle && this.props.updateNoteBundle(0);
        });
    }

    private editableRef: HTMLDivElement | null = null;

    private setEditableRef = (el: HTMLDivElement | null) => {
        this.editableRef = el;
    }

    // The contentEditable surface is uncontrolled: its DOM is the live source of truth
    // while the user is typing (formatting comes from the browser's own execCommand, not
    // from re-rendering React children), so its content is only (re)seeded imperatively –
    // never via React children/dangerouslySetInnerHTML – to avoid React's diffing fighting
    // the browser's own in-place edits to the same subtree.
    private resetEditableContent = () => {
        const el = this.editableRef;
        if (!el) {
            return;
        }
        document.execCommand('defaultParagraphSeparator', false, 'br');
        el.innerHTML = noteTextToEditableHtml(this.state.text || '');
    }

    private handleEditableInput = () => {
        const el = this.editableRef;
        if (!el) {
            return;
        }
        // Guarantees the :empty CSS placeholder still matches after the last character is
        // deleted – some browsers leave a stray <br> behind rather than truly emptying it.
        if (el.textContent === '') {
            el.innerHTML = '';
        }
        this.setState({ text: domToNoteText(el), blockUnsavedDialogs: false });
        this.updateActiveFormatsFromSelection();
    }

    private handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();
        const plainText = event.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, plainText);
    }

    private updateActiveFormatsFromSelection = () => {
        if (document.activeElement !== this.editableRef) {
            return;
        }
        try {
            this.setState({
                activeFormats: {
                    bold: document.queryCommandState('bold'),
                    italic: document.queryCommandState('italic'),
                    underline: document.queryCommandState('underline'),
                }
            });
        }
        catch {
            // queryCommandState can throw outside a well-defined editing context in some
            // browsers – active-state highlighting is best-effort, never load-bearing.
        }
    }

    private handleSelectionChange = () => {
        this.updateActiveFormatsFromSelection();
    }

    private applyFormat = (type: FormatType) => {
        const el = this.editableRef;
        if (!el) {
            return;
        }
        el.focus();
        document.execCommand(type === 'bold' ? 'bold' : type === 'italic' ? 'italic' : 'underline');
        this.setState({ text: domToNoteText(el), blockUnsavedDialogs: false });
        this.updateActiveFormatsFromSelection();
    }

    private onBoldClick = () => this.applyFormat('bold');
    private onItalicClick = () => this.applyFormat('italic');
    private onUnderlineClick = () => this.applyFormat('underline');

    handleClickOutsideNote(event: MouseEvent) {
        if(window.innerWidth > 1200){
            if(!this.state.clickOutsideHandlerIsPaused) {
                if (this.noteEditorRef.current && !this.noteEditorRef.current.contains(event.target)) {
                    this.setState({ isInEditMode: false, blockUnsavedDialogs: false });
                }
            }
        }
    };   
    

    componentDidUpdate(prevProps: Readonly<INoteInlineEditorComponentProps>, prevState: Readonly<INoteInlineEditorComponentState>, snapshot?: any): void {

        
        if (prevState.isInEditMode !== this.state.isInEditMode) {

            if (!this.state.isInEditMode) {
                $(".js-add-note").removeClass("active");
            }

            // Global koll på VILKEN anteckning som faktiskt har sin editor öppen just nu –
            // används av "Relaterat material"-filtret så en öppen editor aldrig döljs
            if(this.state.isInEditMode){
                this.props.setEditingNoteId && this.props.setEditingNoteId(this.props.note.id);
            }else if(this.props.editingNoteId === this.props.note.id){
                this.props.setEditingNoteId && this.props.setEditingNoteId(null);
            }

            if(this.state.isInEditMode){
                $('body').addClass('editIsOpen');
                this.resetEditableContent();
                this.editableRef?.focus();
                document.addEventListener('selectionchange', this.handleSelectionChange);

                if(this.props.noteCategories.length < 1){
                    const service = new NoteService();
        service.getNoteCategories((data) => {
            this.props.setNoteCategories && this.props.setNoteCategories(data)
        }, (err) => {
            console.log('could not get categories', err)
        })
                }

                if($("html").hasClass("isZoomedIn")){
                    if( $("#editorHeadline").offset()?.top){
                        let pos = $("#editorHeadline").offset()?.top;
                        if(pos){
                            pos -= 210;
                            $("html, body").animate({ scrollTop: pos }, 500);
                        }
                    }
                }
            }else{
                document.removeEventListener('selectionchange', this.handleSelectionChange);
                if($('body').hasClass('editIsOpen')){
                    $('body').removeClass('editIsOpen');
                }
                if((prevProps.note.title !== this.state.title) || (prevProps.note.text !== this.state.text)){
                    if(!this.state.unsavedChangesDialogOpen && !this.state.blockUnsavedDialogs){
                        this.setState({
                            unsavedChangesDialogOpen: true
                        });
                    }
                }
            }
        }
        if(prevProps.overlapsSibling !== this.props.overlapsSibling){
            this.setState({
                isInExpandedMode: !this.props.overlapsSibling
            });
        }

        if(prevProps.forceEditNote !== this.props.forceEditNote){
            if(this.props.forceEditNote?.id === this.props.note.id){
                this.setState({
                    isInEditMode: true
                })
            }
        }

        if(prevProps.note !== this.props.note){
            this.updateClickEventToBundledElements();
        }

        if(prevProps.categories !== this.props.categories){
            this.props.setNoteCategories && this.props.setNoteCategories(this.props.categories)
        }

        // Textändringar (bredd/display hanteras av ResizeObservern på text-elementet).
        if(prevState.text !== this.state.text || prevProps.note.text !== this.props.note.text || prevState.textExpanded !== this.state.textExpanded){
            requestAnimationFrame(() => this.measureTextOverflow());
        }

        if(prevState.isInExpandedMode !== this.state.isInExpandedMode){
            const thisElement = $("#note-editor-" + this.props.note.id);
            if(thisElement.hasClass("isBundled")){
                if(this.state.isInExpandedMode){
                    thisElement.addClass("highestInDeck");
                }else{
                    thisElement.removeClass("highestInDeck");
                }
            }
        }
        
    }

    componentDidMount() {
        if (this.editableRef != null) {
            this.editableRef.focus();
        }
        if(this.props.note.openEditorAtStart) {
            // Detta setState triggar componentDidUpdate:s isInEditMode-diff (prevState
            // från konstruktorn var false), som sätter editingNoteId – inget extra behövs här.
            this.setState({ isInEditMode: true });
        }

        $("#" + this.props.note.id).on("click", ()=>{

            if(!this.props.note.text && !this.props.note.title){
                if(window.innerWidth > 1200){
                    this.onToggleEditClick();
                }
            }else{
                this.setState({
                    isInExpandedMode: true,
                })
            }
        });

        this.updateClickEventToBundledElements()

        document.addEventListener('mousedown', this.handleClickOutsideNote, true);
        requestAnimationFrame(() => this.measureTextOverflow());
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutsideNote, true);
        document.removeEventListener('selectionchange', this.handleSelectionChange);
        this.resizeObserver?.disconnect();

        if(this.state.isInEditMode && this.props.editingNoteId === this.props.note.id){
            this.props.setEditingNoteId && this.props.setEditingNoteId(null);
        }
    }

    private updateClickEventToBundledElements() {;
        const thisElement = $("#note-editor-" + this.props.note.id);
        thisElement.on("click", (e)=>{
            if(!thisElement.parent().hasClass("highestInDeck")){
                const arr = $('[data-colliding="'+ this.props.belongsToBundle +'"]');
                if(!!arr){
                    arr.map((i, item)=>{
                        if(!!$("#b-" + item.id) && !$("#b-" + item.id).find(".dropdown").hasClass("open")){
                            item && $("#b-" + item.id).click();
                        }else{
                            console.warn("could not find a bundle with id: ", "#b-" + item.id);
                        }
                    });
                    
                }
            }
        });
    }

    private scrollNoteIntoView(note: INoteData) {
        if(note.elementId === "#"){
            return;
        }
        const $target = $(note.elementId);
        if ($target.length !== 1) {
            return;
        }
        
        const pageBottomY = window.scrollY + window.innerHeight;
        const noteEditor = document.getElementById(`note-editor-${note.id}`);
        if(!noteEditor) {
            return;
        }

        const noteEditorY = noteEditor.getBoundingClientRect().top + window.scrollY;
        const editorBottomY = noteEditorY + noteEditor.offsetHeight;        
        if(editorBottomY > pageBottomY){
            let scrollY = editorBottomY - pageBottomY;
            scrollY += 20; //add some extra to get some space below the editor   
            var currentPositionOfPage = window.scrollY;     
            $("html, body").animate({ scrollTop: currentPositionOfPage + scrollY }, 500);       
        }
    }

    private onCreateClick = () => {
        const note = objectAssign({},
            this.props.note,
            {
                color: this.state.color,
                text: this.state.text,
                title: this.state.title
            });

        if (this.props.disableEditor) {
            this.props.disableEditor();
        }

        const service = new NoteService();

        Toaster.show("Anteckning sparad", true);

        service.createNote(note,
            (note: INoteData) => {
                note.elementId = `#${note.markupId}`;
                this.onNoteCreated(note);
            },
            () => {
                alert('Ett fel har uppstått. Anteckningen kunde inte sparas');
                if (this.props.enableEditor) {
                    this.props.enableEditor();
                }
            });
    }

    private onSaveClick = () => {

        this.setState({
            blockUnsavedDialogs: true
        });
        this.setState({
            text: this.props.note.text,
            title: this.props.note.title,
            unsavedChangesDialogOpen: false,
        });
        const note = objectAssign({},
            this.props.note,
            {
                color: this.state.color,
                text: this.state.text,
                title: this.state.title
            });

        if (this.props.disableEditor) {
            this.props.disableEditor();
        }

        if (this.state.selectedCategoryId) {
            note.categoryId = this.state.selectedCategoryId;
        }

        const service = new NoteService();
        
        Toaster.show("Anteckning sparad", true);

        
        service.updateNote(note,
            this.onNoteSaved,
            () => {
                alert('Ett fel har uppstått. Anteckningen kunde inte sparas');
                if (this.props.enableEditor) {
                    this.props.enableEditor();
                }
            });

        $(".highestInDeck").removeClass("highestInDeck");
        this.updateNoteBundles();

    }

    private updateNoteBundles(){
        this.props.updateNoteBundle && this.props.updateNoteBundle(this.props.updateNoteCollisionAndBundling ? this.props.updateNoteCollisionAndBundling + 1 : 1);
    }

    private onCancelClick = () => {

        if(this.props.onCancelClick || this.props.onCancel){
            $(".noteOverlay").hide();
            actionCreators.setNotesPanelExpaned(false);
        }
        if (this.props.onCancelClick) {
            this.props.onCancelClick();
        }
        if (this.props.onCancel) {
            this.props.onCancel();
        }
        $(".highestInDeck").removeClass("highestInDeck");
    }

    private onDeleteClick = () => {   
        if (this.props.onDelete) {
            this.setState({ clickOutsideHandlerIsPaused: true });
            this.props.onDelete();
            this.updateNoteBundles();
        }
        $(".js-add-note").removeClass("active");
    }

    private onToggleEditClick = () => {   

        $(".js-add-note").removeClass("active");

        if(window.innerWidth < 1200){
            this.setState({ isInEditMode: !this.state.isInEditMode });
            if(this.state.isInEditMode){
                this.props.forceEditNoteFunc && this.props.forceEditNoteFunc(null);
            }
        }else{
            if (this.state.isInEditMode) {
                this.setState({ isInEditMode: false });
            } else {
                this.setState({ isInEditMode: true });
            }
            this.setState({ isInExpandedMode: false });
        }
        this.scrollNoteIntoView(this.props.note);

    }

    private onToggleExpandedClick = () => {
        this.setState({isInExpandedMode: !this.state.isInExpandedMode});
    }

    private onNoteCreated = (note: INoteData) => {
        if (this.props.onNoteCreated) {
            this.props.onNoteCreated(note);
        }
    }

    private onNoteSaved = (note: INoteData) => {
        if (note.selection != null && note.selection.length > 0) {
            const uniqueClass = `marking-${this.props.note.id}`;
            $(`.${uniqueClass}`).attr('class', `marking  marking--selected marking--${note.color}  ${uniqueClass}`);
        }

        this.setState({ isInEditMode: false, text: note.text, title: note.title });
        
        if (this.props.onNoteSaved) {
            this.props.onNoteSaved(note);
        }
        
        if (this.props.onSaveSuccess) {
            this.props.onSaveSuccess();
        }
    }

    private onNewCategoyClick = () => {
        this.setState({ clickOutsideHandlerIsPaused: true });
        this.setState({ isCreateCategoryDialogOpen: true });
    }

    private closeNewCategoryDialog = () => {
        this.setState({ clickOutsideHandlerIsPaused: false });
        this.setState({ isCreateCategoryDialogOpen: false });
    }

    private onNewCategoryAdded = (category) => {
        this.props.setNoteCategories && this.props.setNoteCategories([...this.props.noteCategories, { categoryId: category.id, categoryName: category.name, notes: [], numberOfNotes: 0}]);
        this.setState({ selectedCategoryId: category.id, isCreateCategoryDialogOpen: false });
        this.setState({ clickOutsideHandlerIsPaused: false });
    }


    private handleTitleChange = (event) => {
        this.setState({ title: event.target.value, blockUnsavedDialogs: false });
    }

    private handleChangeCategory = (event) => {
        const categoryId = event.target.value;
        const { note } = this.props;

        this.setState({ selectedCategoryId: categoryId });

        if(note.id) {
            const noteService = new NoteService();
            noteService.updateCategoryForNote(note.id, categoryId);
        }
    }

    private handleChangeColor = (color: string) => {
        this.setState({ color: color });
    }

    renderEditButtons() {
        return (
            <div className="note-editor__buttons">
                <button className="note-editor__button edit" onClick={()=> { this.onSaveClick(); this.onCancelClick()}}>Spara</button>
                <button className="note-editor__button remove" onClick={this.onDeleteClick} aria-label='Radera'><span>Radera</span></button>
            </div>  
        );
    }

    renderViewText() {
        return (
            <div className="note-editor__view">
                <div
                    ref={this.setTextContentRef}
                    className={`note-editor__text-content ${!this.state.textExpanded ? 'note-editor__text-content--clamped' : ''}`}
                >
                    {this.state.text ? noteTextToReact(this.state.text) : <i className="note-editor__marking-label">Markering</i>}
                </div>
                {this.state.textOverflows &&
                    <button
                        type="button"
                        className="note-editor__show-more"
                        onClick={this.onToggleTextExpanded}
                        aria-expanded={this.state.textExpanded}
                    >
                        {this.state.textExpanded ? 'Visa mindre' : 'Visa mer'}
                    </button>
                }
            </div>
        );
    }

    private revertUnsavedChangesDialogOpen = ()=>{
        
        this.setState({
            unsavedChangesDialogOpen: false,
            text: this.props.note.text,
            title: this.props.note.title
        });
    }
    private saveUnsavedChanges = ()=>{
        this.onSaveClick();
        this.setState({
            unsavedChangesDialogOpen: false
        });
    }

    render() {
        const { note, noteCategories } = this.props;

        const colors = ['color1', 'color2', 'color3', 'color4', 'color5'];
        return (<>
            {<>

            <div ref={this.noteEditorRef} tabIndex={0} id={`note-editor-${note.id}`} data-belongstobundle={this.props.belongsToBundle} className={`note-editor noteEditorFold ${this.props.isBundled ? 'isBundled' : ''} ${this.state.isInExpandedMode ? 'expandedMode' : ''} ${this.props.note.color} ${this.props.notesPanelExpaned ? 'panelIsExpaned': '' } ${this.state.isInEditMode ? 'editOpen' : ''}`}>
                <div className="note-editor__wrapper">

                    {this.state.isInEditMode &&   
                        <>    
                            <div className={`note-editor__edit note-editor__edit-close`} onClick={this.onToggleEditClick} tabIndex={0} onKeyUp={(e) => { if (e.key === "Enter") { this.onToggleEditClick(); } }}></div>
                            {/*<label className="note-editor__label" htmlFor="editorHeadline">Rubrik:</label> 
                            {<input id="editorHeadline" value={this.state.title || ''} type="text" className={`note-editor__title`} onChange={this.handleTitleChange} placeholder="Rubrik..." />*/}                                       

                        </>
                    }
                    {!this.state.isInEditMode &&   
                        <>
                            {/*(this.props.note.title || this.props.note.text) ? <>
                            
                                {this.props.note.title ?<strong className='textEllipsis'>{this.props.note.title}</strong> : <i className='textEllipsis'>{this.props.note.text}</i>}
                            </> : <>
                                <div className="notePen_empty"/>
                            </>*/}
                            
                            <div className="note-editor__edit" onClick={this.onToggleEditClick} tabIndex={!this.props.isBundled ? 0 : -1} onKeyUp={(e) => { if (e.key === "Enter") { this.onToggleEditClick(); } }}></div>
                        </>
                    }

                    {this.state.isInEditMode &&
                        <>
                            <div className="color-selector__label" aria-label="markeringsfärg">Markeringsfärg</div>
                            <div className="color-selector">
                                <div className="color-selector__colors">
                                    {colors.map((color) => {
                                        return <div tabIndex={0} aria-label={color} onKeyUp={(e) => { e.key === "Enter" && this.handleChangeColor(color) }} onClick={() => this.handleChangeColor(color)} className={`color-selector__color  color-selector__color--${color}  ${this.state.color === color ? 'color-selector__color--selected' : null}`} key={color}></div>
                                    })}
                                </div>
                            </div>
                        </>
                    }     

                    {(this.state.isInEditMode) && 
                        <div className="note-editor__editor-area">
                            <label className="note-editor__label" htmlFor="editorDescription">Anteckning</label>
                            { note.original && <div className="note-editor__original">{note.original}</div> }
                            <div className="note-editor__text-area-wrapper">
                                <div
                                    id="editorDescription"
                                    ref={this.setEditableRef}
                                    className="note-editor__text-area"
                                    contentEditable
                                    suppressContentEditableWarning={true}
                                    role="textbox"
                                    aria-multiline={true}
                                    aria-label="Anteckning"
                                    data-placeholder="Skriv anteckning här..."
                                    onInput={this.handleEditableInput}
                                    onPaste={this.handlePaste}
                                    onKeyUp={this.updateActiveFormatsFromSelection}
                                    onMouseUp={this.updateActiveFormatsFromSelection}
                                    onFocus={this.updateActiveFormatsFromSelection}
                                />
                                <div className="note-editor__format-toolbar" role="toolbar" aria-label="Textformatering">
                                    <button type="button" className={`note-editor__format-button ${this.state.activeFormats.bold ? 'note-editor__format-button--active' : ''}`} aria-pressed={this.state.activeFormats.bold} onMouseDown={(e) => e.preventDefault()} onClick={this.onBoldClick} aria-label="Fet text" title="Fet text"><BoldIcon /></button>
                                    <button type="button" className={`note-editor__format-button ${this.state.activeFormats.italic ? 'note-editor__format-button--active' : ''}`} aria-pressed={this.state.activeFormats.italic} onMouseDown={(e) => e.preventDefault()} onClick={this.onItalicClick} aria-label="Kursiv text" title="Kursiv text"><ItalicIcon /></button>
                                    <button type="button" className={`note-editor__format-button ${this.state.activeFormats.underline ? 'note-editor__format-button--active' : ''}`} aria-pressed={this.state.activeFormats.underline} onMouseDown={(e) => e.preventDefault()} onClick={this.onUnderlineClick} aria-label="Understruken text" title="Understruken text"><UnderlineIcon /></button>
                                </div>
                            </div>
                        </div>
                        }

                    {!this.state.isInEditMode &&
                        <div className="note-editor__editor-area">
                            {this.renderViewText()}
                        </div>
                    }

                    {/*this.state.isInEditMode && 
                        <div className="note-editor__category">
                            <label className="note-editor__label" htmlFor="selectCategoryDropdown">Kategori:</label>
                            <div className="note-editor__category-wrapper">
                                <select id="selectCategoryDropdown" value={this.state.selectedCategoryId} className='note-editor__dropdown' tabIndex={0} onChange={this.handleChangeCategory}>
                                    {this.props.noteCategories.map((category, index) => (
                                        <option value={category.categoryId} key={index}>{category.categoryName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="bookmarks__add-button note-editor__category-add-button" onClick={this.onNewCategoyClick} tabIndex={0} onKeyDown={(e)=>{ e.key === "Enter" && this.onNewCategoyClick()}} title="Skapa ny kategori" aria-label="Skapa ny kategori"><span>Lägg till kategori</span></div>
                        </div>
                    */}

                        
                </div>    

                {this.state.isInEditMode &&
                    this.renderEditButtons()  
                }   
                
            </div>
            { this.state.unsavedChangesDialogOpen && <UnsavedChangesModal regardingNote={this.props.note} newTitle={this.state.title} newText={this.state.text} saveChanges={this.saveUnsavedChanges} revertChanges={this.revertUnsavedChangesDialogOpen} />}
            { this.state.isCreateCategoryDialogOpen && <CreateNewCategoryModal closeNewCategoryDialog={this.closeNewCategoryDialog} onNewCategoryAdded={this.onNewCategoryAdded} />}
            
            </>}
        </>);
    }
}

// #endregion Component

// #region Redux mappers

function mapStateToProps(state: IStateTree, ownProps): INoteInlineEditorComponentProps {
    return { 
        updateNoteCollisionAndBundling: state.document.updateNoteCollisionAndBundling,
        editorDisabled: state.document.editorDisabled,
        note: ownProps.note,
        noteCategories: state.document.noteCategories,
        forceEditNote: state.document.forceEditNote,
        editingNoteId: state.document.editingNoteId,
        notesPanelExpaned: state.document.notesPanelExpaned,
    }
}

function mapDispatchToProps(dispatch): INoteInlineEditorComponentDispatch {
    return {
        disableEditor: () => {
            dispatch(actionCreators.disableEditor());
        },
        enableEditor: () => {
            dispatch(actionCreators.enableEditor());
        },
        onCancelClick: () => {
            // dispatch(actionCreators.hideToolbar());
        },
        updateNoteBundle: (index: number) => {
            dispatch(actionCreators.updateNoteCollisionAndBundling(index));
        },
        onNoteCreated: (note: INoteData) => {
            dispatch(actionCreators.addNote(note));
        },
        onNoteSaved: (note: INoteData) => {
            note.openEditorAtStart = false;
            dispatch(actionCreators.refreshNote(note));
            dispatch(actionCreators.aNoteWasUpdated());
        },
        hideToolbar: () => {
            dispatch(actionCreators.hideToolbar());
        },
        setNoteCategories: (categories) => {
            dispatch(actionCreators.setNoteCategories(categories));
        },
        setUpdateNotes: () => {
            dispatch(actionCreators.aNoteWasUpdated());
        },
        forceEditNoteFunc: ()=>{
            dispatch(actionCreators.forceEditNote(null));
        },
        setEditingNoteId: (noteId: string | null) => {
            dispatch(setEditingNoteId(noteId));
        }
    }
}

// #endregion Redux mappers

// #region Smart container

export default connect(mapStateToProps, mapDispatchToProps)(NoteInlineEditorComponent);

// #endregion Smart container
